import { stopPushForAuthChange } from './pushClient';

// Central auth storage. Reasons it exists:
//
// 1. The app used to call `localStorage.getItem('jwtToken')` /
//    `setItem('jwtToken', ...)` from a dozen places (oauth, email,
//    chat socket, settings, etc). Any future auth change had to touch
//    all of them.
// 2. We now support multiple logged-in accounts (Instagram-style
//    "switch account" dropdown). Instead of a single `jwtToken` key
//    we keep a list of accounts and an active-account id.
//
// Backwards-compatible: if a user has the old `jwtToken` key but
// nothing in `jwtAccounts`, we treat that token as a single-account
// list on first read, so no existing session is logged out.
//
// Backwards-mirror: whenever the active token changes we ALSO keep
// the legacy `jwtToken` key in sync. If this whole file is reverted
// (and all call sites with it), nothing breaks — the old code path
// just reads the mirrored key as before.

const LEGACY_KEY = 'jwtToken';
const ACCOUNTS_KEY = 'jwtAccounts';
const ACTIVE_KEY = 'jwtActiveAccountId';

const readAccounts = () => {
  try {
    const raw = localStorage.getItem(ACCOUNTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeAccounts = (list) => {
  try { localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(list)); } catch {}
};

const readActiveId = () => {
  try { return localStorage.getItem(ACTIVE_KEY) || null; } catch { return null; }
};

const writeActiveId = (id) => {
  try {
    if (id) localStorage.setItem(ACTIVE_KEY, id);
    else localStorage.removeItem(ACTIVE_KEY);
  } catch {}
};

// Keep the legacy single-key JWT in sync with the active account.
// Guarantees that reverting this file still leaves the user signed
// in: axiosInstance's old `localStorage.getItem('jwtToken')` path
// finds the same token.
const mirrorLegacy = (token) => {
  try {
    if (token) localStorage.setItem(LEGACY_KEY, token);
    else localStorage.removeItem(LEGACY_KEY);
  } catch {}
};

// Migrate on first access: if the user has a legacy jwtToken but no
// accounts list yet, synthesize a single-entry list from it. We
// don't yet know the user's id/name/avatar — the account is tagged
// with a placeholder id and will be enriched the first time
// updateMyUserInfo() runs after this (see `updateActiveAccountInfo`).
const ensureMigrated = () => {
  const list = readAccounts();
  if (list.length > 0) return list;
  let legacy = null;
  try { legacy = localStorage.getItem(LEGACY_KEY); } catch {}
  if (!legacy) return [];
  const synthetic = [{
    id: 'legacy',       // overwritten by updateActiveAccountInfo
    token: legacy,
    userId: null,
    userName: '',
    avatarUrl: '',
  }];
  writeAccounts(synthetic);
  writeActiveId('legacy');
  return synthetic;
};

export const listAccounts = () => {
  return ensureMigrated();
};

export const getActiveAccount = () => {
  const list = ensureMigrated();
  if (list.length === 0) return null;
  const activeId = readActiveId();
  return list.find(a => a.id === activeId) || list[0] || null;
};

// Primary getter used by axiosInstance interceptor.
export const getActiveToken = () => {
  const a = getActiveAccount();
  return a ? a.token : null;
};

// Called when ANY login flow (email code / Google / Telegram / signup)
// obtains a fresh JWT. Appends to the accounts list and makes it
// active. If an account with the same userId already exists its
// token is replaced in place.
export const setActiveToken = async (token, info = {}) => {
  await stopPushForAuthChange();
  if (!token) return;
  const list = readAccounts();
  const userId = info.userId || null;
  const id = userId || `token-${Date.now()}`;

  // De-dupe by userId when known — a re-login replaces the stale
  // token for that account rather than adding a second copy.
  const next = list.filter(a => (userId ? a.userId !== userId : true));
  next.unshift({
    id,
    token,
    userId,
    userName: info.userName || '',
    avatarUrl: info.avatarUrl || '',
  });

  // Also sweep out the "legacy" synthetic entry — now that we know
  // the real id, the legacy row is a duplicate.
  const cleaned = next.filter(a => a.id !== 'legacy' || a.token === token);
  writeAccounts(cleaned);
  writeActiveId(id);
  mirrorLegacy(token);
};

// Called after updateMyUserInfo() succeeds — the real userId / name
// / avatar came back. Replaces the active entry in place; mostly
// matters for the migrated-from-legacy case where we didn't know who
// the token belonged to.
export const updateActiveAccountInfo = (info = {}) => {
  const list = readAccounts();
  const activeId = readActiveId();
  if (list.length === 0) return;
  const idx = list.findIndex(a => a.id === activeId);
  if (idx < 0) return;
  const current = list[idx];
  const newId = info.userId || current.userId || current.id;
  const next = [...list];
  next[idx] = {
    ...current,
    id: newId,
    userId: info.userId || current.userId,
    userName: info.userName || current.userName || '',
    avatarUrl: info.avatarUrl || current.avatarUrl || '',
  };
  // If the id changed (legacy → real userId), rewrite activeId too.
  writeAccounts(next);
  if (newId !== activeId) writeActiveId(newId);
};

// Enrich a stored (non-active) account's cached userName/avatarUrl.
// Used by the ⋯ switch-account menu to backfill rows that were saved
// before we started passing info to setActiveToken.
export const updateAccountInfo = (accountId, info = {}) => {
  const list = readAccounts();
  const idx = list.findIndex(a => a.id === accountId);
  if (idx < 0) return;
  const current = list[idx];
  const next = [...list];
  next[idx] = {
    ...current,
    userId: info.userId || current.userId,
    userName: info.userName || current.userName || '',
    avatarUrl: info.avatarUrl || current.avatarUrl || '',
  };
  writeAccounts(next);
};

// Pick a different stored account to be active. Returns the new
// token (or null if the id was unknown).
export const switchToAccount = async (accountId) => {
  await stopPushForAuthChange();
  const list = readAccounts();
  const target = list.find(a => a.id === accountId);
  if (!target) return null;
  writeActiveId(target.id);
  mirrorLegacy(target.token);
  return target.token;
};

// Remove the currently active account from the list. If other
// accounts remain, promote the first of them to active (and return
// its token). Otherwise clears everything and returns null.
export const removeActiveAccount = async () => {
  await stopPushForAuthChange();
  const list = readAccounts();
  const activeId = readActiveId();
  const remaining = list.filter(a => a.id !== activeId);
  writeAccounts(remaining);
  if (remaining.length === 0) {
    writeActiveId(null);
    mirrorLegacy(null);
    return null;
  }
  writeActiveId(remaining[0].id);
  mirrorLegacy(remaining[0].token);
  return remaining[0].token;
};

// Nuke everything — used for a "sign out of all accounts" flow and
// also kept in case anything still calls localStorage.removeItem
// directly during a transition.
export const clearAllAccounts = async () => {
  await stopPushForAuthChange();
  writeAccounts([]);
  writeActiveId(null);
  mirrorLegacy(null);
};
